// Putting runner. Per-opponent transfer model: every putting event is
// settled head-to-head with each other player in the round. In a
// fourball at $1 per point, a player who chips in (0-putt) wins $2
// from each of the three opponents = +$6 to them, -$2 to each opponent;
// a player who 3-putts pays $1 to each opponent = -$3 to them, +$1 to
// each opponent. Zero-sum across all participants.
//
// Per-opponent rates:
//   • 0 PUTT (chip-in / off-green)                         → +2
//   • 1 PUTT                                               → +1
//   • UD (par-or-better, bunker up-and-down, 1 putt)       → +2
//   • 3 PUTT (or pickup, counted as 3)                     → -1
//
// A 1UD hole fires the U&D event ONLY — its 2-point award already
// reflects the closing 1-putt, so it never also tallies in the 1 PUTT
// column. The click-to-expand list for "1 putt" therefore excludes
// any hole the user marked as 1UD.
//
// Settlement modes:
//   • Bet per point (default). Each event triggers the symmetric
//     transfer above, scaled by pointBet. Per-category buckets carry
//     the player's NET money flow for that category (own gains + losses
//     to others' events of the same category). Bucket counts and the
//     click-to-expand hole list cover the player's OWN events only.
//   • Nassau. Three pots — front 9, back 9, overall 18 — sized
//     `players.length × bet`. Highest segment-point total wins each
//     pot; ties split. Segment points use the rates above (own-event
//     scoring only; no transfer). Per-category bucket money stays at 0
//     in Nassau mode and the leaderboard switches to OUT/IN/TOTAL.

import { Course, Hole, Player, ScoreMap } from "../types";

export interface PuttingOptions {
  betPerPoint?: number;
  nassau?: boolean;
  overallBet?: number;
  frontNineBet?: number;
  backNineBet?: number;
  // Non-Nassau bet mode. When false the round settles as a pot —
  // each player antes `overallBet` and the highest intrinsic-point
  // total takes the pot (ties split). When true (default) the
  // existing per-opponent point transfers fire at `betPerPoint`.
  useBetPerPoint?: boolean;
}

export interface PuttingEventBucket {
  // Count of THIS player's own events in the category.
  count: number;
  // 1-based hole numbers where this player's own events fired.
  holes: number[];
  // Bet-per-point: net money flow for this player in this category.
  // Nassau: 0 (segment settlement uses pot logic instead).
  winnings: number;
}

export interface PuttingPayout {
  // Sum of intrinsic event points (the per-opponent rate, not scaled).
  // Drives Nassau segment settlement; otherwise informational.
  points: number;
  total: number;
  zeroPutts: PuttingEventBucket;
  onePutts: PuttingEventBucket;
  threePutts: PuttingEventBucket;
  uAndD: PuttingEventBucket;
  overall: number;
  front: number;
  back: number;
}

export interface PuttingResult {
  payouts: Record<string, PuttingPayout>;
  nassauActive: boolean;
  hasAnyBet: boolean;
}

export interface PuttingInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: PuttingOptions;
}

type Category = "zeroPutts" | "onePutts" | "threePutts" | "uAndD";

// Per-opponent rate (also used as Nassau segment-point value).
function rateFor(cat: Category): number {
  if (cat === "zeroPutts") return 2;
  if (cat === "uAndD") return 2;
  if (cat === "threePutts") return -1;
  return 1; // onePutts
}

// Decide which event fires for a given (player, hole) score. A 1UD
// score (1 putt + bunkerUd, gross === par) maps to U&D alone — the
// 2-point award supersedes the standard 1-putt event, and the hole
// never appears in the 1 PUTT bucket.
function categoriesFor(
  score: { putts?: number; gross: number; bunkerUd?: boolean },
  hole: Hole,
): Category[] {
  if (typeof score.putts !== "number") return [];
  const putts = score.putts;
  if (putts === 0) return ["zeroPutts"];
  if (putts === 1) {
    if (score.bunkerUd && score.gross === hole.par) return ["uAndD"];
    return ["onePutts"];
  }
  if (putts >= 3) return ["threePutts"];
  return []; // 2 putts → no event
}

export function runPutting(input: PuttingInput): PuttingResult {
  const { course, players, scores, options } = input;
  // Default to bet-per-point so legacy rounds (saved before the toggle
  // existed) keep their head-to-head behaviour.
  const useBetPerPoint = options.useBetPerPoint !== false;
  const nassau = !!options.nassau;
  // Per-point rate only fires in bet-per-point + non-Nassau mode.
  const pointBet =
    !nassau && useBetPerPoint ? Number(options.betPerPoint) || 0 : 0;
  // Bet-per-player pot only fires in non-Nassau + non-per-point mode.
  const playerBet =
    !nassau && !useBetPerPoint ? Number(options.overallBet) || 0 : 0;
  const overallBet = nassau ? Number(options.overallBet) || 0 : 0;
  const frontBet = nassau ? Number(options.frontNineBet) || 0 : 0;
  const backBet = nassau ? Number(options.backNineBet) || 0 : 0;

  const payouts: Record<string, PuttingPayout> = {};
  for (const p of players) {
    payouts[p.id] = {
      points: 0,
      total: 0,
      zeroPutts: { count: 0, holes: [], winnings: 0 },
      onePutts: { count: 0, holes: [], winnings: 0 },
      threePutts: { count: 0, holes: [], winnings: 0 },
      uAndD: { count: 0, holes: [], winnings: 0 },
      overall: 0,
      front: 0,
      back: 0,
    };
  }

  // Per-(player, hole) intrinsic point contribution, used for Nassau
  // segment math. Filled alongside the per-event accounting below.
  const segContrib: Record<string, Record<number, number>> = {};
  for (const p of players) {
    segContrib[p.id] = {};
    for (const h of course.holes) segContrib[p.id][h.number] = 0;
  }

  // Single pass: for every (player, hole) score, derive categories,
  // tally own buckets, accumulate Nassau segment points, and (in
  // bet-per-point mode) apply the symmetric per-opponent transfer.
  for (const hole of course.holes) {
    for (const p of players) {
      const sc = scores[p.id]?.[hole.number];
      if (!sc) continue;
      const cats = categoriesFor(sc, hole);
      if (cats.length === 0) continue;

      const opponents = players.filter((op) => op.id !== p.id);

      for (const cat of cats) {
        const rate = rateFor(cat);

        // Own count / holes / intrinsic points (always, both modes).
        payouts[p.id][cat].count += 1;
        payouts[p.id][cat].holes.push(hole.number);
        payouts[p.id].points += rate;
        segContrib[p.id][hole.number] += rate;

        // Bet-per-point transfer: each opponent pays `rate × pointBet`
        // to the player. Skipped in Nassau mode (pots settle later)
        // and when no opponents exist (single-player edge case).
        if (!nassau && pointBet > 0 && opponents.length > 0) {
          payouts[p.id][cat].winnings +=
            rate * pointBet * opponents.length;
          for (const op of opponents) {
            payouts[op.id][cat].winnings -= rate * pointBet;
          }
        }
      }
    }
  }

  if (nassau) {
    // Per-segment intrinsic-point totals per player.
    const segPoints = (
      pid: string,
      inSeg: (h: number) => boolean,
    ): number => {
      let total = 0;
      for (const h of course.holes) {
        if (inSeg(h.number)) total += segContrib[pid][h.number] || 0;
      }
      return total;
    };

    const settle = (
      segFor: (pid: string) => number,
      bet: number,
      bucket: "overall" | "front" | "back",
    ) => {
      if (bet <= 0) return;
      const seg = players.map((p) => ({ id: p.id, pts: segFor(p.id) }));
      if (seg.length === 0) return;
      const maxPts = Math.max(...seg.map((s) => s.pts));
      const winners = seg.filter((s) => s.pts === maxPts);
      const pot = seg.length * bet;
      const share = pot / winners.length;
      for (const sp of seg) {
        payouts[sp.id][bucket] +=
          sp.pts === maxPts ? share - bet : -bet;
      }
    };

    settle((pid) => segPoints(pid, () => true), overallBet, "overall");
    settle(
      (pid) => segPoints(pid, (h) => h >= 1 && h <= 9),
      frontBet,
      "front",
    );
    settle(
      (pid) => segPoints(pid, (h) => h >= 10 && h <= 18),
      backBet,
      "back",
    );

    for (const p of players) {
      const pay = payouts[p.id];
      pay.total = pay.overall + pay.front + pay.back;
    }
  } else if (pointBet > 0) {
    // Bet-per-point: total is the sum of all per-category transfers
    // (which already captured both own gains and losses-to-others on
    // the same pass).
    for (const p of players) {
      const pay = payouts[p.id];
      pay.total =
        pay.zeroPutts.winnings +
        pay.onePutts.winnings +
        pay.threePutts.winnings +
        pay.uAndD.winnings;
    }
  } else if (playerBet > 0) {
    // Bet-per-player: every player antes `playerBet`. Highest intrinsic
    // point total wins the pot; ties split. Per-category buckets stay
    // at 0 (the cash sits on .total only — same convention Nassau uses
    // for its segment buckets).
    const seg = players.map((p) => ({ id: p.id, pts: payouts[p.id].points }));
    if (seg.length > 0) {
      const maxPts = Math.max(...seg.map((s) => s.pts));
      const winners = seg.filter((s) => s.pts === maxPts);
      const pot = seg.length * playerBet;
      const share = pot / winners.length;
      for (const sp of seg) {
        payouts[sp.id].total +=
          sp.pts === maxPts ? share - playerBet : -playerBet;
      }
    }
  }

  return {
    payouts,
    nassauActive: nassau,
    hasAnyBet:
      pointBet > 0 ||
      playerBet > 0 ||
      overallBet > 0 ||
      frontBet > 0 ||
      backBet > 0,
  };
}
